import { useState } from 'react';
import {
  Clock, Megaphone, MessageSquare, Pencil, Plus, Smartphone, TriangleAlert, Users, X,
} from 'lucide-react';
import {
  getApiError,
  useCancelNoticeMutation,
  useListGroupsQuery,
  useListNoticesQuery,
  type Notice,
  type NoticeStatus,
} from '@/store/api';
import { useAuthStore } from '@/store/auth.store';
import { Mark } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import { ConfirmDialog } from '@/components/ui/ConfirmDialog';
import { EmptyState } from '@/components/ui/EmptyState';
import { Select } from '@/components/ui/Field';
import { PageLayout } from '@/components/ui/PageLayout';
import { Spinner } from '@/components/ui/Spinner';
import { cn } from '@/lib/cn';
import { styles } from '@/styles';
import { NoticeDialog, emptyNoticeDraft, toNoticeDraft, type NoticeDraft } from './NoticeDialog';
import { NoticeReachDialog } from './NoticeReachDialog';

const stampFmt = new Intl.DateTimeFormat('en-GH', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' });

/*
  The notice board.

  ── Why publishing is two clicks and not one ───────────────────────────────

  This is the only screen in SafePass where one press writes to every parent in
  the school at once. So the button on the row opens the reach dialog, and the
  publish button lives inside it, underneath the count of who it reaches and
  what it costs. The extra click is the point.
*/
export function NoticesPage() {
  const institutionId = useAuthStore((s) => s.user?.institutionId ?? '');

  const [status, setStatus] = useState<NoticeStatus | ''>('');
  const [draft, setDraft] = useState<NoticeDraft | null>(null);
  const [reachFor, setReachFor] = useState<Notice | null>(null);
  const [withdrawing, setWithdrawing] = useState<Notice | null>(null);
  const [banner, setBanner] = useState<{ tone: 'success' | 'danger'; text: string } | null>(null);

  const { data: groups = [] } = useListGroupsQuery(institutionId, { skip: !institutionId });
  const { data: notices = [], isLoading, isError, error } = useListNoticesQuery(
    { institutionId, status: status || undefined },
    { skip: !institutionId },
  );
  const [cancelNotice, { isLoading: withdrawPending }] = useCancelNoticeMutation();

  const unsent = notices.filter((n) => n.status === 'draft').length;

  const handleWithdraw = async () => {
    if (!withdrawing) return;
    try {
      await cancelNotice({ institutionId, noticeId: withdrawing._id }).unwrap();
      setBanner({ tone: 'success', text: `"${withdrawing.title}" is no longer visible to parents.` });
    } catch (err) {
      setBanner({ tone: 'danger', text: getApiError(err) });
    }
    setWithdrawing(null);
  };

  return (
    <PageLayout
      title="Notice board"
      subtitle="Announcements that reach every parent at once"
      breadcrumbs={[{ label: 'Communication' }, { label: 'Notice board' }]}
      contained={false}
      actions={
        <Button icon={<Plus size={13} />} onClick={() => setDraft(emptyNoticeDraft())}>
          New notice
        </Button>
      }
      toolbar={
        <>
          <Select value={status} onChange={(e) => setStatus(e.target.value as NoticeStatus | '')} className="w-auto">
            <option value="">All notices</option>
            <option value="draft">Drafts</option>
            <option value="scheduled">Scheduled</option>
            <option value="published">Published</option>
            <option value="cancelled">Withdrawn</option>
          </Select>
          {unsent > 0 && status !== 'draft' && (
            <button
              type="button"
              onClick={() => setStatus('draft')}
              className={cn(styles.markWarning, 'normal-case tracking-normal text-[11px]')}
            >
              {unsent} unsent draft{unsent === 1 ? '' : 's'}
            </button>
          )}
        </>
      }
    >
      <div className="flex flex-col gap-3">
        {banner && (
          <div
            className={cn(
              banner.tone === 'success' ? styles.markSuccess : styles.markDanger,
              'w-full justify-between normal-case tracking-normal text-[11px]',
            )}
          >
            <span className="flex items-center gap-1.5">
              {banner.tone === 'danger' && <TriangleAlert size={12} className="flex-shrink-0" />}
              {banner.text}
            </span>
            <button type="button" onClick={() => setBanner(null)} className="font-bold opacity-60 hover:opacity-100">
              Dismiss
            </button>
          </div>
        )}

        {isLoading ? (
          <div className={cn(styles.panel, 'flex justify-center py-12')}>
            <Spinner />
          </div>
        ) : isError ? (
          <div className={cn(styles.panel, 'overflow-clip')}>
            <EmptyState icon={<TriangleAlert size={17} />} title="Could not load notices" description={getApiError(error)} />
          </div>
        ) : notices.length === 0 ? (
          <div className={cn(styles.panel, 'overflow-clip')}>
            <EmptyState
              icon={<Megaphone size={17} />}
              title={status ? 'Nothing here' : 'No notices yet'}
              description={
                status
                  ? 'Try a different status.'
                  : 'Write an announcement for the whole school or one class. It is saved as a draft until you publish it.'
              }
              action={
                !status ? (
                  <Button size="sm" variant="secondary" icon={<Plus size={12} />} onClick={() => setDraft(emptyNoticeDraft())}>
                    New notice
                  </Button>
                ) : undefined
              }
            />
          </div>
        ) : (
          notices.map((notice) => (
            <NoticeCard
              key={notice._id}
              notice={notice}
              onEdit={() => setDraft(toNoticeDraft(notice))}
              onReach={() => setReachFor(notice)}
              onWithdraw={() => setWithdrawing(notice)}
            />
          ))
        )}
      </div>

      <NoticeDialog draft={draft} groups={groups} institutionId={institutionId} onClose={() => setDraft(null)} />
      <NoticeReachDialog
        notice={reachFor}
        institutionId={institutionId}
        onClose={() => setReachFor(null)}
        onPublished={(text) => setBanner({ tone: 'success', text })}
      />
      <ConfirmDialog
        open={withdrawing !== null}
        onClose={() => setWithdrawing(null)}
        onConfirm={handleWithdraw}
        loading={withdrawPending}
        destructive
        title="Withdraw notice"
        confirmLabel="Withdraw"
        body={
          <>
            <strong>{withdrawing?.title}</strong> will disappear from every parent's app.
            <br />
            <br />
            Any SMS already sent cannot be recalled — but the record of what went out, and to whom,
            is kept.
          </>
        }
      />
    </PageLayout>
  );
}

function NoticeCard({
  notice,
  onEdit,
  onReach,
  onWithdraw,
}: {
  notice: Notice;
  onEdit: () => void;
  onReach: () => void;
  onWithdraw: () => void;
}) {
  const withdrawn = notice.status === 'cancelled';
  const live = notice.status === 'published';

  return (
    <div className={cn(styles.panel, withdrawn && 'opacity-70')}>
      <div className="flex flex-wrap items-start justify-between gap-3 px-4 py-3">
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <h2 className={cn('text-[13px] font-bold text-foreground', withdrawn && 'line-through')}>
              {notice.title}
            </h2>
            {notice.priority === 'urgent' && <Mark tone="danger">Urgent</Mark>}
            {notice.status === 'draft' && <Mark tone="warning">Draft</Mark>}
            {notice.status === 'scheduled' && <Mark tone="info">Scheduled</Mark>}
            {live && <Mark tone="success">Published</Mark>}
            {withdrawn && <Mark tone="neutral">Withdrawn</Mark>}
          </div>

          <p className="mt-1.5 max-w-2xl whitespace-pre-line text-[11.5px] leading-relaxed text-muted-foreground">
            {notice.body.length > 240 ? `${notice.body.slice(0, 240)}…` : notice.body}
          </p>

          <div className={cn(styles.meta, 'mt-1.5 flex flex-wrap items-center gap-x-3 gap-y-1')}>
            <span className="flex items-center gap-1">
              <Users size={11} />
              {notice.groupNames.length === 0 ? 'Whole school' : notice.groupNames.join(', ')}
            </span>
            {notice.scheduledFor && !live && (
              <span className="flex items-center gap-1">
                <Clock size={11} /> {stampFmt.format(new Date(notice.scheduledFor))}
              </span>
            )}
            {live && notice.publishedAt && <span>Sent {stampFmt.format(new Date(notice.publishedAt))}</span>}
            {notice.sendPush && (
              <span className="flex items-center gap-1">
                <Smartphone size={11} /> Push{live && ` · ${notice.delivery.pushSent} sent`}
              </span>
            )}
            {notice.sendSms && (
              <span className="flex items-center gap-1">
                <MessageSquare size={11} /> SMS
                {live && ` · ${notice.delivery.smsSent} sent`}
                {live && notice.delivery.smsFailed > 0 && (
                  <span className="text-[hsl(var(--record-ink-danger))]">, {notice.delivery.smsFailed} failed</span>
                )}
              </span>
            )}
          </div>
        </div>

        <div className="flex flex-shrink-0 items-center gap-2">
          <Button size="sm" variant={live || withdrawn ? 'ghost' : 'primary'} onClick={onReach}>
            {live || withdrawn ? 'Where it went' : 'Review & publish'}
          </Button>
          {!live && !withdrawn && (
            <Button size="sm" variant="ghost" iconOnly aria-label={`Edit ${notice.title}`} onClick={onEdit}>
              <Pencil size={13} />
            </Button>
          )}
          {!withdrawn && (
            <Button size="sm" variant="ghost" iconOnly aria-label={`Withdraw ${notice.title}`} onClick={onWithdraw}>
              <X size={14} />
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}
